import React, { useState } from 'react';

interface ExpireRenewDialogProps {
  apiUrl: string;
  authToken: string;
  license: {
    id: string;
    product: string;
    clientName: string | null;
    validUntil: string;
  };
  action: 'expire' | 'renew';
  onClose: () => void;
  onSuccess: () => void;
}

const ExpireRenewDialog: React.FC<ExpireRenewDialogProps> = ({ 
  apiUrl, 
  authToken, 
  license, 
  action, 
  onClose, 
  onSuccess 
}) => {
  const [validUntil, setValidUntil] = useState(
    action === 'expire' 
      ? new Date().toISOString().slice(0, 16) // Current date/time for expire
      : new Date(new Date(license.validUntil).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16) // 1 year from now for renew
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(
        `${apiUrl}/api/licenses/${encodeURIComponent(license.id)}/${encodeURIComponent(license.product)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Basic ${authToken}`,
          },
          body: JSON.stringify({
            validUntil: validUntil,
          }),
        }
      );

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(data.error || `Failed to ${action} license`);
      }
    } catch (err) {
      setError(`Failed to ${action} license`);
    } finally {
      setLoading(false);
    }
  };

  const actionLabel = action === 'expire' ? 'Expire' : 'Renew';
  const actionDescription = action === 'expire' 
    ? 'Set the license expiration date to now or a past date to expire it immediately.'
    : 'Extend the license validity period.';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold mb-4">{actionLabel} License</h2>
        
        <div className="mb-4 p-3 bg-gray-50 rounded-md">
          <p className="text-sm text-gray-600 mb-1">
            <span className="font-medium">Identifier:</span> {license.id}
          </p>
          <p className="text-sm text-gray-600 mb-1">
            <span className="font-medium">Product:</span> {license.product}
          </p>
          {license.clientName && (
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-medium">Client:</span> {license.clientName}
            </p>
          )}
          <p className="text-sm text-gray-600">
            <span className="font-medium">Current Valid Until:</span>{' '}
            {new Date(license.validUntil).toLocaleString()}
          </p>
        </div>

        <p className="text-sm text-gray-600 mb-4">{actionDescription}</p>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label htmlFor="validUntil" className="block text-sm font-medium text-gray-700 mb-2">
              New Valid Until <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              id="validUntil"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50 ${
                action === 'expire' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {loading ? `${actionLabel === 'Expire' ? 'Expiring' : 'Renewing'}...` : actionLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpireRenewDialog;

